import { Badge, Chip, Drawer, Toolbar, useMediaQuery } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon, { IconProps } from '../components/shared/Icon';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { capitalizeFirstLetter } from '../utils/capitalize';
import { getTodayPeriod } from '../utils/dates';
import { permissionLevels, systemConfigKeyEnum } from '../utils/enums';
import './SideBar.css';
import { useSystemConfig } from '../hooks/useSystemConfig';

export interface NavbarGroup {
    MAIN: { name: string; link: string; permsLevel: number; hide?: boolean };
    PAGES: { name: string; link: string; permsLevel: number; iconName: IconProps['name']; isNew?: boolean }[];
}

interface NavBarProps {
    expanded: boolean;
    toggleSidebar: () => void;
}

const NavBar: React.FC<NavBarProps> = ({ expanded, toggleSidebar }) => {
    const { muiTheme } = useTheme();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const location = useLocation();
    const navigate = useNavigate();

    const DASHBOARD: NavbarGroup = {
        MAIN: { name: t(''), link: '/', permsLevel: permissionLevels.CONDUC, hide: true },
        PAGES: [
            {
                name: capitalizeFirstLetter(t('dashboard')),
                link: '/',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'spaceDashboard',
            },
        ],
    };

    const OPERATIONS: NavbarGroup = {
        MAIN: { name: t('operations'), link: '/operations', permsLevel: permissionLevels.CHAUFFEUR },
        PAGES: [
            {
                name: capitalizeFirstLetter(t('needs')),
                link: '/operations/needs',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'sendTimeExtension',
            },
            {
                name: capitalizeFirstLetter(t('planning')),
                link: '/operations/planning',
                permsLevel: permissionLevels.CHAUFFEUR,
                iconName: 'editCalendar',
            },
            {
                name: capitalizeFirstLetter(t('supervision')),
                link: '/operations/supervision',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'preview',
            },
            {
                name: capitalizeFirstLetter(t('validation')),
                link: '/operations/validation',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'freeCancellation',
            },
            {
                name: capitalizeFirstLetter(t('summary')),
                link: '/operations/summary',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'tableChart',
            },
            {
                name: capitalizeFirstLetter(t('reporting')),
                link: '/operations/reporting',
                permsLevel: permissionLevels.ADMIN,
                iconName: 'equalizer',
            },
            {
                name: capitalizeFirstLetter(t('expenses')),
                link: '/operations/expenses',
                permsLevel: permissionLevels.CONDUC,
                iconName: 'euro',
            },
            { name: t('gnr').toUpperCase(), link: '/operations/gnr', permsLevel: permissionLevels.PLANNING, iconName: 'localGasStation' },
        ],
    };

    const QHSE: NavbarGroup = {
        MAIN: { name: t('qhse'), link: '/qhse', permsLevel: permissionLevels.ADMIN },
        PAGES: [
            {
                name: capitalizeFirstLetter(t('forms')),
                link: '/qhse/forms',
                permsLevel: permissionLevels.SUPERVISION,
                iconName: 'listAlt',
            },
        ],
    };

    const SALES: NavbarGroup = {
        MAIN: { name: t('sales'), link: '/sales', permsLevel: permissionLevels.ETUDES, hide: true },
        PAGES: [
            {
                name: t('pli').toUpperCase(),
                link: '/sales/pli',
                permsLevel: permissionLevels.ETUDES,
                iconName: 'monetizationOn',
            },
        ],
    };

    const ADMIN: NavbarGroup = {
        MAIN: { name: t('admin'), link: '/admin', permsLevel: permissionLevels.PLANNING },
        PAGES: [
            {
                name: capitalizeFirstLetter(t('configuration')),
                link: '/admin/configuration',
                permsLevel: permissionLevels.ADMIN,
                iconName: 'settings',
            },
            {
                name: capitalizeFirstLetter(t('internal')),
                link: '/admin/internal',
                permsLevel: permissionLevels.PLANNING,
                iconName: 'adminPanelSettings',
            },
            {
                name: capitalizeFirstLetter(t('material')),
                link: '/admin/material',
                permsLevel: permissionLevels.PLANNING,
                iconName: 'precisionManufacturing',
            },
            {
                name: capitalizeFirstLetter(t('vehicles')),
                link: '/admin/vehicles',
                permsLevel: permissionLevels.PLANNING,
                iconName: 'airportShuttle',
            },
            { name: capitalizeFirstLetter(t('external')), link: '/admin/external', permsLevel: permissionLevels.PLANNING, iconName: 'business' },
        ],
    };

    const sections: NavbarGroup[] = [DASHBOARD, OPERATIONS, QHSE, SALES, ADMIN];

    const getLogoByPeriod = () => {
        const period = getTodayPeriod(new Date());

        if (period === 'normal') return require('../assets/images/my-sltp-transp.png');
        if (period === 'halloween') return require('../assets/images/my-sltp-transp-halloween.png');
        if (period === 'christmas') return require('../assets/images/my-sltp-transp-christmas.png');
        if (period === 'newYear') return require('../assets/images/my-sltp-transp-newyear.png');

        return require('../assets/images/my-sltp-transp.png');
    };

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? expanded : true}
            onClose={toggleSidebar}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: expanded || isMobile ? '240px' : '60px',
                transition: muiTheme.transitions.create('width', {
                    easing: muiTheme.transitions.easing.sharp,
                    duration: muiTheme.transitions.duration.enteringScreen,
                }),
                flexShrink: 0,
                position: 'sticky',

                '& .MuiDrawer-paper': {
                    width: expanded || isMobile ? '240px' : '60px',
                    transition: muiTheme.transitions.create('width', {
                        easing: muiTheme.transitions.easing.sharp,
                        duration: muiTheme.transitions.duration.enteringScreen,
                    }),
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                    border: 0,
                    backgroundColor: '#000',
                },
            }}
        >
            <div className="fx-c h-100 fx-items-c of-y-auto ">
                {!isMobile && <Toolbar />}
                <div className={`fx-c fx-items-c fx-fg-1 pad-5px of-y-auto text-white pad-t-10px gap-20px w-100`}>
                    {isMobile && expanded && <img style={{ height: 30 }} src={getLogoByPeriod()} alt="MY SLTP" title={'MY SLTP'} />}
                    <div className="of-y-auto fx-c fx-fg-1 w-100 gap-15px ">
                        {sections.map((section) => {
                            if (!user || section.MAIN.permsLevel < user.profileId.permsLevel || !section.PAGES.length) return null;
                            return (
                                <div className="font-w-700" key={section.MAIN.link}>
                                    {/*expanded ? section.MAIN.name.toUpperCase() : section.MAIN.name ? '...' : ''*/}
                                    <div className="fx-c gap-5px pad-l-10px pad-r-10px pad-t-5px">
                                        {section.PAGES.map(
                                            (page) =>
                                                page.permsLevel >= user.profileId.permsLevel &&
                                                (() => {
                                                    const regex = new RegExp(`^${page.link}(/|$)`);
                                                    const isActive = regex.test(location.pathname);

                                                    return (
                                                        <div
                                                            className="fx-r fx-items-c sidebar-item"
                                                            style={{
                                                                backgroundColor: isActive ? 'white' : undefined,
                                                                color: isActive ? 'black' : 'white',
                                                                fontWeight: isActive ? 'bold' : 'normal',
                                                            }}
                                                            key={page.link}
                                                            onClick={() => {
                                                                navigate(page.link);
                                                                if (isMobile) toggleSidebar();
                                                            }}
                                                        >
                                                            <div className="fx-r fx-items-c fx-fg-1 w-100">
                                                                <div className="fx-r fx-items-c gap-5px fx-fg-1" title={page.name}>
                                                                    <Badge variant="dot" color="info" invisible={!page.isNew || expanded}>
                                                                        <Icon
                                                                            name={page.iconName}
                                                                            sx={{
                                                                                color: !isActive
                                                                                    ? muiTheme.palette.common.white
                                                                                    : muiTheme.palette.common.black,
                                                                            }}
                                                                        />
                                                                    </Badge>
                                                                    {expanded && <div className="one-line">{page.name}</div>}
                                                                </div>
                                                            </div>
                                                            {expanded && (
                                                                <div className="fx-r fx-justify-fe fx-items-c">
                                                                    {page.isNew && <Chip label="New" sx={{ height: 'auto' }} color="info" />}
                                                                    {isActive && <Icon name="keyboardArrowRightSharp" color="secondary" />}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
export default NavBar;
